import { Box, HStack, Text } from "@chakra-ui/react";
import { CornerDownLeft } from "lucide-react";
import { iconSize } from "~/components/ui/icon-size";
import { formatTimeAgoCompact } from "~/utils/formatTimeAgo";
import { getIconInfo, type ListItem } from "../getIconInfo";

interface CommandItemProps {
  item: ListItem;
  index: number;
  isSelected: boolean;
  onSelect: (item: ListItem) => void;
  onMouseEnter: (index: number) => void;
  /** The option's id, which the search field points at while it is highlighted. */
  id: string;
}

/** The id of the palette option at `index` in the list `listId`. */
export function commandOptionId({
  listId,
  index,
}: {
  listId: string;
  index: number;
}): string {
  return `${listId}-option-${index}`;
}

/**
 * One result in the palette, as a Ledger list row: 32px, a 14px icon in
 * Text 3, the name in Text and a quieter description in Text 3 that
 * truncates; the highlighted row sits on surface-3 (its description steps
 * up to Text 2) with the Enter mark at the end. Recent items show how long
 * ago they were opened.
 *
 * Spec: specs/design-system/ledger-shell.feature
 */
export function CommandItem({
  item,
  index,
  isSelected,
  onSelect,
  onMouseEnter,
  id,
}: CommandItemProps) {
  const { Icon } = getIconInfo(item);

  let label = "";
  let description: string | undefined;

  if (item.type === "command") {
    label = item.data.label;
    description = item.data.description;
  } else if (item.type === "search") {
    label = item.data.label;
    description = item.data.description;
  } else if (item.type === "recent") {
    label = item.data.label;
    description = item.data.description;
  } else if (item.type === "project") {
    label = item.data.name;
    description = item.data.orgTeam;
  }

  return (
    <HStack
      id={id}
      role="option"
      aria-selected={isSelected}
      data-index={index}
      height="var(--row-h)"
      paddingX="var(--space-3)"
      marginX="var(--space-2)"
      gap="var(--space-3)"
      cursor="pointer"
      borderRadius="var(--radius-2)"
      background={isSelected ? "var(--color-surface-3)" : "transparent"}
      onClick={() => onSelect(item)}
      onMouseEnter={() => onMouseEnter(index)}
    >
      <Box
        color={isSelected ? "var(--color-fg-2)" : "var(--color-fg-3)"}
        flexShrink={0}
        display="flex"
      >
        <Icon style={iconSize.sm} aria-hidden />
      </Box>
      <HStack flex={1} gap="var(--space-2)" overflow="hidden" minWidth={0}>
        <Text
          fontSize="var(--text-13)"
          fontWeight="medium"
          color="var(--color-fg)"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          flexShrink={0}
          maxWidth="70%"
        >
          {label}
        </Text>
        {description && (
          <Text
            fontSize="var(--text-12)"
            color={isSelected ? "var(--color-fg-2)" : "var(--color-fg-3)"}
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {description}
          </Text>
        )}
      </HStack>
      {item.type === "recent" && (
        <Text
          fontSize="var(--text-12)"
          color={isSelected ? "var(--color-fg-2)" : "var(--color-fg-3)"}
          fontVariantNumeric="tabular-nums"
          flexShrink={0}
        >
          {formatTimeAgoCompact(item.data.accessedAt)}
        </Text>
      )}
      {isSelected && (
        <Box color="var(--color-fg-2)" flexShrink={0} display="flex">
          <CornerDownLeft style={iconSize.sm} aria-hidden />
        </Box>
      )}
    </HStack>
  );
}
